import { useCallback, useEffect, useState } from 'react';
import { requireSupabase } from './supabase.js';
import { bidReportTarget, blockBidSupplier, blockListingOwner, listBlocks, listingReportTarget, maxReportDetails, reportContent, reportReasons, unblockEntry } from './safety.js';

const blockedOn = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/** Report and block buttons for one listing or bid. `block` is { label, confirm, run }. */
export function SafetyActions({ targetType, targetId, reportLabel = 'Report', block, onBlocked }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const send = async (event) => {
    event.preventDefault(); setBusy(true); setError('');
    try { await reportContent(requireSupabase(), { targetType, targetId, reason, details }); setOpen(false); setReason(''); setDetails(''); setNotice('Report sent. We review reports within 24 hours.'); }
    catch (cause) { setError(cause.message); } finally { setBusy(false); }
  };
  const runBlock = async () => {
    if (!block || !window.confirm(block.confirm)) return;
    setBusy(true); setError('');
    try { await block.run(); await onBlocked?.(); } catch (cause) { setError(cause.message); } finally { setBusy(false); }
  };
  return <div className="coord-safety">
    <div className="coord-inline-actions">
      <button type="button" className="text-link" onClick={() => setOpen(!open)} aria-expanded={open}>{reportLabel}</button>
      {block && <button type="button" className="text-link" disabled={busy} onClick={runBlock}>{block.label}</button>}
    </div>
    {open && <form className="coord-form" onSubmit={send}>
      <label>Reason<select required value={reason} onChange={(e) => setReason(e.target.value)}><option value="">Choose a reason</option>{reportReasons.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      <label>Details (optional)<textarea rows="3" maxLength={maxReportDetails} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Anything that helps us check this"/></label>
      <p>The person you report is not told who reported them.</p>
      <button className="button button-secondary" disabled={busy || !reason}>{busy ? 'Sending…' : 'Send report'}</button>
    </form>}
    {notice && <p className="account-save-status" role="status">{notice}</p>}
    {error && <p className="account-form-error" role="alert">{error}</p>}
  </div>;
}

/** Supplier-side actions on a feed listing ('job' | 'home_job' | 'tender'). */
export function ListingSafetyActions({ kind, id, onBlocked }) {
  const home = kind === 'home_job';
  return <SafetyActions
    targetType={listingReportTarget[kind]}
    targetId={id}
    reportLabel="Report listing"
    onBlocked={onBlocked}
    block={{
      label: home ? 'Block this poster' : 'Block this buyer',
      confirm: home
        ? "Block this poster? You won't see their jobs any more, and they won't see yours."
        : "Block this buyer? You won't see this organisation's jobs or tenders any more, and your company can't bid on them.",
      run: () => blockListingOwner(requireSupabase(), kind, id),
    }}
  />;
}

/** Owner-side actions on a bid ('job' | 'home_job' | 'tender'). */
export function BidSafetyActions({ kind, id, supplierName, onBlocked }) {
  return <SafetyActions
    targetType={bidReportTarget[kind]}
    targetId={id}
    reportLabel="Report bid"
    onBlocked={onBlocked}
    block={{
      label: 'Block supplier company',
      confirm: `Block ${supplierName || 'this company'}? You won't see its bids any more, and it won't see your organisation's jobs or tenders.`,
      run: () => blockBidSupplier(requireSupabase(), kind, id),
    }}
  />;
}

/** "Blocked" list for the account page. */
export function BlockedList() {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const load = useCallback(async () => {
    try { const result = await listBlocks(requireSupabase()); setEntries([...result.people, ...result.organizations]); setError(''); }
    catch (cause) { setError(cause.message); }
  }, []);
  useEffect(() => { void Promise.resolve().then(load); }, [load]);
  const remove = async (entry) => {
    setBusy(entry.key);
    try { await unblockEntry(requireSupabase(), entry); setEntries((current) => current.filter((item) => item.key !== entry.key)); }
    catch (cause) { setError(cause.message); } finally { setBusy(''); }
  };
  return <section className="account-content-panel coord-list-panel">
    <div className="account-panel-heading"><div><span className="account-kicker">SAFETY</span><h2>Blocked</h2></div><span>{entries ? entries.length : ''}</span></div>
    {error && <p className="account-form-error" role="alert">{error}</p>}
    {!entries ? <p>Loading blocked people…</p> : !entries.length ? <div className="account-empty"><strong>No one blocked</strong><p>You can block someone from a job, a tender or a bid.</p></div>
      : <div className="coord-owned-list">{entries.map((entry) => <article key={entry.key}><div><strong>{entry.title}</strong><small>Blocked {blockedOn(entry.createdAt)}{entry.byCompany ? ` by ${entry.byCompany}` : ''}</small></div>{entry.canUnblock && <button className="button button-secondary" disabled={busy === entry.key} onClick={() => remove(entry)}>Unblock</button>}</article>)}</div>}
  </section>;
}
